/* Curated add-ons. Shopify remains the source of truth for prices and discounts. */
(function () {
  'use strict';
  const tiers = [{ threshold: 10000, discount: 2000 }, { threshold: 20000, discount: 5000 }];
  const discountFor = (amount) => amount >= tiers[1].threshold ? 5000 : amount >= tiers[0].threshold ? 2000 : 0;
  const tierTitles = ['Spend $100, Save $20', 'Spend $200, Save $50'];

  function cartState(cart) {
    const items = cart.items || [];
    const eligible = items.reduce((sum, item) => sum + (item.gift_card || item.selling_plan_allocation ? 0 : item.original_line_price), 0);
    const subtotal = items.reduce((sum, item) => sum + item.original_line_price, 0);
    const discount = discountFor(eligible);
    // cart/change.js nests applications; cart.js returns the same fields directly.
    const applications = (cart.cart_level_discount_applications || []).map((item) => item.discount_application || item);
    const ordinaryItems = items.every((item) => !item.selling_plan_allocation && item.original_line_price === item.final_line_price);
    // Shopify currently returns an empty type for this store's automatic order discounts.
    const knownDiscounts = applications.length === (discount ? 1 : 0) && applications.every((item) =>
      item.title === tierTitles[discount === 5000 ? 1 : 0] && ['', 'automatic'].includes(item.type) &&
      item.value_type === 'fixed_amount' && item.total_allocated_amount === discount);
    const safe = cart.currency === 'USD' && ordinaryItems && knownDiscounts && subtotal - cart.total_price === discount;
    const next = tiers.find((tier) => eligible < tier.threshold);
    return { eligible, subtotal, discount, safe, next, remaining: next ? next.threshold - eligible : 0 };
  }

  function projection(cart, price) {
    const state = cartState(cart);
    if (!state.safe) return null;
    const afterDiscount = discountFor(state.eligible + price);
    const total = state.subtotal + price - afterDiscount;
    return { total, discount: afterDiscount, extraSaving: afterDiscount - state.discount, extraCost: total - cart.total_price };
  }

  function variantModels(title) {
    const models = String(title).toLowerCase().match(/911|718|boxster|cayman|cayenne|macan|panamera|taycan/g) || [];
    return [...new Set(models.map((model) => /boxster|cayman/.test(model) ? '718' : model))];
  }

  function candidates(products, cart, model, filter) {
    const inCart = new Set((cart.items || []).map((item) => String(item.product_id)));
    const remaining = cartState(cart).remaining;
    return products.filter((product) => !inCart.has(String(product.id)) && (model === 'all' || product.models.includes(model)))
      .map((product) => ({ ...product, variants: product.variants.filter((variant) => {
        const models = variantModels(variant.title);
        return variant.available && (model === 'all' || !models.length || models.includes(model)) &&
          (filter !== 'under40' || variant.price <= 4000) && (filter !== 'over40' || variant.price > 4000);
      }) }))
      .filter((product) => product.variants.length)
      .sort((a, b) => {
        const score = (product) => Math.min(...product.variants.map((variant) => variant.price >= remaining ? variant.price - remaining : 100000 + remaining - variant.price));
        return score(a) - score(b);
      });
  }

  function cartModel(cart) {
    const models = [...new Set(cart.items.flatMap((item) => variantModels(item.variant_title)))];
    return models.length === 1 ? models[0] : 'all';
  }

  function reminder(products, cart) {
    const state = cartState(cart);
    if (!cart.item_count || !state.safe || !state.next) return null;
    // Suggest a tier only when an available, additional item can reach it.
    const canReach = candidates(products, cart, cartModel(cart), 'best')
      .some((product) => product.variants.some((variant) => variant.price >= state.remaining));
    return canReach ? { remaining: state.remaining, extraSaving: state.next.discount - state.discount } : null;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { cartState, projection, candidates, discountFor, reminder };
  if (typeof document === 'undefined') return;
  const dialog = document.querySelector('[data-savings-helper]');
  const data = document.getElementById('SavingsHelperData');
  if (!dialog || !data || typeof dialog.showModal !== 'function') return;
  const config = JSON.parse(data.textContent);
  if (config.currency !== 'USD') return;
  const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  const find = (selector) => dialog.querySelector(selector);
  const list = find('[data-helper-list]');
  const message = find('[data-helper-message]');
  const selection = new Map();
  let cart = null;
  let model = 'all';
  let modelChosen = false;
  let filter = 'best';
  let busy = false;
  let uncertain = false;
  let openSequence = 0;
  let opener;

  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  function setBusy(value) {
    busy = value;
    dialog.setAttribute('aria-busy', String(value));
    dialog.querySelectorAll('button').forEach((button) => { button.disabled = value; });
    if (!value) list.querySelectorAll('[data-add-product]').forEach((button) => { button.disabled = uncertain || !selection.has(button.dataset.addProduct); });
  }
  async function readCart() {
    const response = await fetch(config.root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
    if (!response.ok) throw new Error('Cart unavailable');
    const result = await response.json();
    if (result.currency !== 'USD') throw new Error('Currency changed. Please reload your cart.');
    return result;
  }
  const reminders = document.querySelectorAll('[data-savings-reminder]');
  let reminderSequence = 0;
  let cartChanging = false;
  let pendingQuantity = false;
  function hideReminders() {
    ++reminderSequence;
    reminders.forEach((element) => { element.hidden = true; });
  }
  function updateReminders(fresh) {
    hideReminders();
    if (cartChanging || pendingQuantity) return;
    const value = reminder(config.products, fresh);
    if (!value) return;
    reminders.forEach((element) => {
      element.querySelector('[data-savings-reminder-text]').textContent = `You're ${money(value.remaining)} away from ${money(value.extraSaving)} more off.`;
      element.hidden = false;
    });
  }
  async function refreshReminders() {
    hideReminders();
    if (!reminders.length || cartChanging || pendingQuantity) return;
    const sequence = reminderSequence;
    try {
      const fresh = await readCart();
      if (sequence === reminderSequence) updateReminders(fresh);
    } catch (_) { /* Checkout stays available when the reminder cannot be verified. */ }
  }
  document.addEventListener('cart:changing', () => { cartChanging = true; hideReminders(); });
  document.addEventListener('cart:updated', (event) => { cartChanging = false; updateReminders(event.detail.cart); });
  document.addEventListener('cart:update-failed', () => { cartChanging = false; hideReminders(); });
  document.querySelectorAll('.cart-page input[name="updates[]"]').forEach((input) => {
    const changed = () => { pendingQuantity = true; hideReminders(); };
    input.addEventListener('input', changed);
    input.addEventListener('change', changed);
  });
  document.querySelectorAll('.cart-item__content .text-link, .drawer-cart-item__remove').forEach((link) => link.addEventListener('click', hideReminders));
  document.querySelectorAll('[data-drawer-open="cart"]').forEach((button) => button.addEventListener('click', refreshReminders));
  window.addEventListener('pagehide', hideReminders);
  window.addEventListener('pageshow', (event) => { if (event.persisted) refreshReminders(); });
  refreshReminders();
  function estimateText(variant) {
    if (!variant) return 'Select your exact vehicle and specification to see the estimate.';
    const result = projection(cart, variant.price);
    if (!result) return 'Your cart has a different discount or purchase type. Checkout will confirm the best available offer.';
    return (result.extraSaving > 0 ? `Unlock ${money(result.extraSaving)} more off. ` : '') +
      `Estimated order total: ${money(result.total)} · ${money(result.extraCost)} more to pay.`;
  }
  function render() {
    const state = cartState(cart);
    find('[data-helper-current]').textContent = `Current cart: ${money(cart.total_price)} USD`;
    find('[data-helper-summary]').textContent = !state.safe ? 'Explore add-ons for your cart' : state.next ? `Add ${money(state.remaining)} to reach ${money(state.next.discount)} off` : 'You have reached the highest savings tier';
    find('[data-helper-detail]').textContent = state.safe ?
      (state.next ? `${money(state.eligible)} eligible subtotal · ${state.discount ? money(state.discount) + ' off in your cart' : 'Automatic savings start at $100'}.` : '$50 off in your cart. You are ready to check out.') :
      'Your current discount or purchase type may change which offer applies. Checkout confirms your savings.';
    const progress = find('[data-helper-progress]');
    progress.value = Math.min(20000, state.eligible);
    progress.hidden = !state.safe;
    progress.setAttribute('aria-label', `${money(state.eligible)} of $200 eligible subtotal`);
    list.replaceChildren();
    if (!state.next && state.safe) {
      list.append(node('p', 'savings-helper__empty', 'No extra item is needed to reach the top tier.'));
      return;
    }
    const products = candidates(config.products, cart, model, filter);
    if (!products.length) list.append(node('p', 'savings-helper__empty', 'No additional items match these filters. Try another price range or model.'));
    products.forEach((product) => {
      const card = node('article', 'savings-product');
      if (product.image) {
        const img = node('img', 'savings-product__image');
        img.src = product.image; img.alt = product.title; img.width = 120; img.height = 120; img.loading = 'lazy';
        card.append(img);
      }
      const body = node('div', 'savings-product__body');
      const title = node('a', 'savings-product__title', product.title); title.href = product.url;
      const prices = product.variants.map((variant) => variant.price);
      const price = node('p', 'savings-product__price', `${Math.min(...prices) !== Math.max(...prices) ? 'From ' : ''}${money(Math.min(...prices))} USD`);
      const options = node('fieldset', 'savings-product__options');
      options.append(node('legend', '', 'Choose vehicle & specification'));
      const estimate = node('p', 'savings-product__estimate');
      const add = node('button', 'savings-product__add', 'Choose a specification');
      add.type = 'button'; add.dataset.addProduct = String(product.id); add.disabled = true;
      const selected = product.variants.find((variant) => variant.id === selection.get(String(product.id)));
      if (!selected) selection.delete(String(product.id));
      product.variants.forEach((variant) => {
        const option = node('button', 'savings-variant', variant.title === 'Default Title' ? 'Standard' : variant.title);
        option.type = 'button'; option.setAttribute('aria-pressed', String(selected?.id === variant.id));
        option.addEventListener('click', () => {
          if (busy || uncertain) return;
          selection.set(String(product.id), variant.id);
          options.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button === option)));
          price.textContent = `${money(variant.price)} USD`;
          estimate.textContent = estimateText(variant);
          add.textContent = `Add · ${money(variant.price)}`; add.disabled = false;
        });
        options.append(option);
      });
      estimate.textContent = estimateText(selected);
      if (selected) { price.textContent = `${money(selected.price)} USD`; add.textContent = `Add · ${money(selected.price)}`; add.disabled = uncertain; }
      add.addEventListener('click', () => addProduct(product, selection.get(String(product.id))));
      body.append(title, price, options, estimate, add); card.append(body); list.append(card);
    });
  }

  async function addProduct(product, variantId) {
    if (busy || uncertain || !variantId) return;
    setBusy(true); message.textContent = 'Checking your cart…';
    let before;
    let attempted = false;
    let rejected = false;
    try {
      before = await readCart();
      if (before.items.some((item) => String(item.product_id) === String(product.id))) {
        cart = before; render(); message.textContent = 'This product is already in your cart. Review its quantity in the cart.'; return;
      }
      if (before.total_price !== cart.total_price || before.item_count !== cart.item_count || cartState(before).eligible !== cartState(cart).eligible) {
        cart = before; render(); message.textContent = 'Your cart changed. Review the updated estimate, then add your item.'; return;
      }
      hideReminders();
      message.textContent = 'Adding your selected item…'; attempted = true;
      const response = await fetch(config.root + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }), signal: AbortSignal.timeout(15000)
      });
      rejected = response.status >= 400 && response.status < 500;
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This item could not be added.');
      message.textContent = 'Added. Opening your updated cart…';
      window.location.assign(config.cartUrl);
    } catch (error) {
      if (!attempted) { message.textContent = 'We could not refresh your cart. Please try again or open your cart.'; return; }
      try {
        const after = await readCart();
        const quantity = (value) => value.items.reduce((sum, item) => sum + (String(item.variant_id) === String(variantId) ? item.quantity : 0), 0);
        if (quantity(after) > quantity(before)) {
          message.textContent = 'The item is in your cart. Opening your cart…'; window.location.assign(config.cartUrl); return;
        }
        cart = after;
        // A timed-out request can still finish later. Never invite an automatic or blind retry.
        uncertain = !rejected;
        render();
        message.textContent = rejected ? `${error.message} Please review the product page for availability.` : 'We could not confirm the update. Open your cart before trying again.';
      } catch (_) {
        uncertain = true;
        message.textContent = 'We could not confirm the update. Open your cart before trying again.';
      }
    } finally { setBusy(false); }
  }

  async function open(trigger) {
    if (dialog.open) return;
    opener = trigger; const sequence = ++openSequence;
    dialog.showModal(); document.documentElement.classList.add('savings-helper-open');
    list.replaceChildren(); find('[data-helper-summary]').textContent = 'Checking your savings…';
    find('[data-helper-detail]').textContent = ''; find('[data-helper-current]').textContent = '';
    message.textContent = ''; setBusy(true);
    try {
      const fresh = await readCart();
      if (sequence !== openSequence) return;
      cart = fresh;
      updateReminders(fresh);
      if (!modelChosen) {
        model = cartModel(fresh);
        dialog.querySelectorAll('[data-model-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.modelFilter === model)));
      }
      render();
    } catch (_) { message.textContent = 'We could not load your cart. Close and try again, or use View cart.'; }
    finally { if (sequence === openSequence) setBusy(false); }
  }
  document.querySelectorAll('[data-savings-helper-open]').forEach((trigger) => {
    trigger.addEventListener('click', () => open(trigger));
  });
  find('[data-helper-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('cancel', (event) => { if (busy) event.preventDefault(); });
  dialog.addEventListener('click', (event) => { if (event.target === dialog && !busy) dialog.close(); });
  dialog.addEventListener('close', () => {
    ++openSequence; document.documentElement.classList.remove('savings-helper-open'); opener?.focus();
  });
  dialog.querySelectorAll('[data-price-filter], [data-model-filter]').forEach((button) => {
    button.addEventListener('click', () => {
      if (busy || !cart) return;
      const isModel = button.hasAttribute('data-model-filter');
      if (isModel) { model = button.dataset.modelFilter; modelChosen = true; selection.clear(); } else filter = button.dataset.priceFilter;
      dialog.querySelectorAll(isModel ? '[data-model-filter]' : '[data-price-filter]').forEach((other) => other.setAttribute('aria-pressed', String(button === other)));
      render();
    });
  });
})();
